import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { signInWithSlack } from "../../lib/supabase";
import { loginDestination } from "../../lib/authRedirect";
import { useAuth } from "../../contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SyncSimpleMark } from "@/components/brand/SyncSimpleMark";
import { AmbientPanel } from "@/components/brand/AmbientPanel";

// Only Slack OAuth is actually wired up today (see CLAUDE.md). These are
// inert "coming soon" rows matching the Figma design — real brand icons
// from the design team's asset drop (public/platforms/).
const COMING_SOON_PLATFORMS = [
  { name: "Microsoft Teams", iconSrc: "/platforms/teams.svg" },
  { name: "WhatsApp", iconSrc: "/platforms/whatsapp.svg" },
  { name: "Discord", iconSrc: "/platforms/discord.svg" },
  { name: "Telegram", iconSrc: "/platforms/telegram.svg" },
];

export const LoginForm: React.FC = () => {
  const [slackLoading, setSlackLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Check for error in URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlError = searchParams.get("error");
    if (urlError) {
      setError(decodeURIComponent(urlError));
      // Clear the error from URL
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete("error");
      const newUrl = `${location.pathname}${newSearchParams.toString() ? "?" + newSearchParams.toString() : ""}`;
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const from = loginDestination(location.state);
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  const handleSlackLogin = async () => {
    setSlackLoading(true);
    setError("");

    try {
      // Always use standard Slack OAuth flow without plan selection
      // Users will get 7-day trial automatically
      const redirectTo = loginDestination(location.state);

      await signInWithSlack(`${window.location.origin}${redirectTo}`);
      // Note: The redirect happens automatically with OAuth
    } catch (err: any) {
      console.error("Slack login error:", err);
      setError(err.message || "Failed to sign in with Slack");
      setSlackLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      {/* Left: form. bg-[#FDFCFA] rather than bg-background (paper-50,
          #F5F2EC) is a deliberate, screen-local deviation, matched to the
          approved reference for this screen: the page reads near-white
          while the coming-soon button cards below have a warm off-white tint
          and hand-tuned border — the opposite polarity from bg-background/
          bg-card elsewhere in the app. Not a change to those brand tokens,
          just this split-screen layout. */}
      <div className="flex flex-col justify-between gap-10 bg-[#FFFFFF] px-6 py-10 sm:px-12 lg:px-20 lg:py-[54px]">
        <div className="flex items-center gap-3">
          <SyncSimpleMark className="h-[15px] w-auto" />
          <span className="text-xl font-bold leading-none tracking-[-0.02em] text-[#0A1330]">
            SyncSimple
          </span>
        </div>

        <div className="mx-auto w-full max-w-[400px]">
          <h1 className="text-[28px] font-bold leading-[1.12] tracking-[-0.03em] text-[#0A1330]">
            Create your account
          </h1>
          <p className="mt-3 max-w-[370px] text-[15px] font-medium leading-[1.55] text-[#6F7488]">
            Add SyncSimple to the tool your team already talks in. More
            platforms are on the way.
          </p>

          {error && (
            <Alert variant="destructive" className="mt-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mt-8 flex flex-col gap-3">
            {/* Slack — the only platform that actually works today */}
            <Button
              type="button"
              variant="default"
              onClick={handleSlackLogin}
              disabled={slackLoading}
              className="group relative h-[52px] w-full rounded-[10px] bg-[#081231] px-6 text-sm font-semibold text-white shadow-[0_11px_22px_rgba(10,19,48,0.20),0_2px_5px_rgba(10,19,48,0.18)] transition-[background-color,box-shadow,transform] hover:-translate-y-px hover:bg-[#0E1A40] hover:shadow-[0_14px_26px_rgba(10,19,48,0.22),0_3px_7px_rgba(10,19,48,0.18)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
            >
              <span
                className="absolute left-[22px] top-1/2 flex -translate-y-1/2 items-center"
                aria-hidden="true"
              >
                {slackLoading ? (
                  <Loader2 className="!size-5 animate-spin" />
                ) : (
                  <img src="/Slack_icon_2019.svg" alt="" className="h-5 w-5" />
                )}
              </span>
              <span className="block text-center">
                {slackLoading ? "Connecting to Slack…" : "Continue with Slack"}
              </span>
              {!slackLoading && (
                <ArrowRight
                  className="absolute right-[22px] top-1/2 h-4 w-4 -translate-y-1/2 opacity-80 transition-transform group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              )}
            </Button>

            {/* Coming soon — inert, disabled */}
            {COMING_SOON_PLATFORMS.map(({ name, iconSrc }) => (
              <Button
                key={name}
                type="button"
                variant="outline"
                disabled
                aria-disabled="true"
                className="relative h-[52px] w-full rounded-[10px] border border-[#DED8CC] bg-[#FDFBF7] px-6 text-sm font-semibold text-[#A5ADBE] shadow-[0_8px_18px_rgba(10,19,48,0.06),0_1px_2px_rgba(10,19,48,0.05)] disabled:cursor-not-allowed disabled:opacity-100"
              >
                <span
                  className="absolute left-[22px] top-1/2 -translate-y-1/2 opacity-45 grayscale"
                  aria-hidden="true"
                >
                  <img src={iconSrc} alt="" className="h-5 w-5" />
                </span>
                <span className="block text-center">Add to {name}</span>
                <Badge
                  variant="outline"
                  className="absolute right-[21px] top-1/2 h-5 -translate-y-1/2 rounded-full border-[#E5DFD6] bg-white/80 px-[10px] font-mono text-[9px] font-bold uppercase leading-[18px] tracking-[0.16em] text-[#9CA3B3]"
                >
                  Soon
                </Badge>
              </Button>
            ))}
          </div>

          <p className="mt-7 text-center text-[13px] font-medium leading-none text-[#717789]">
            Already have an account?{" "}
            <Link
              to="/auth/login"
              className="font-bold text-[#147C9C] underline-offset-4 hover:underline"
            >
              Log in
            </Link>
          </p>
        </div>

        <p className="mx-auto w-full max-w-[400px] text-center text-xs font-medium leading-none text-[#B4BAC9]">
          By continuing you agree to SyncSimple's{" "}
          <a
            href="https://syncsimple.ai/terms"
            target="_blank"
            rel="noreferrer"
            className="text-[#2485A2] hover:text-[#0A1330]"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://syncsimple.ai/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-[#2485A2] hover:text-[#0A1330]"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>

      {/* Right: Ink panel — hidden on small screens, no horizontal scroll introduced.
          No text/testimonial here by design: matched to the reference artifact,
          which leaves this panel as pure ambient background. */}
      <div className="relative hidden overflow-hidden bg-ink-900 lg:flex lg:items-center lg:justify-center">
        <AmbientPanel className="absolute inset-0 h-full w-full" />
      </div>
    </div>
  );
};
